// Home (docs/project.md §7; Main / Desktop-Home boards): dictionary switcher, word count, omnibox
// and the list grouped by day. Child routes render as overlays on top of it.
import { useEffect, useMemo, useState } from 'react';
import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useLocale, useTranslations } from 'use-intl';
import { useCards, usePendingAdds } from '@/db/hooks';
import { useDictionaries } from '@/state/session';
import { useOnline } from '@/state/online';
import { useIsDesktop } from '@/lib/useIsDesktop';
import { storage } from '@/lib/storage';
import { dictionaryTitle, studentLabel } from '@/lib/dictionaryName';
import { WordList } from '@/components/WordList';
import { RetiredNotices } from '@/components/RetiredNotices';
import { OfflineBadge } from '@/components/OfflineBadge';
import { DictionaryLists } from '@/components/home/DictionaryLists';
import { DesktopOmnibox } from '@/components/home/DesktopOmnibox';
import { Book, ChevronDown, Gear, Search } from '@/components/Icons';
import { Sheet, SheetBar } from '@/components/ui';
import { setAnalyticsContext } from '@/lib/analytics';
import { languageName } from '@solungo/core/lang';

// No onboarding guard here: an invited teacher has no name and no own dictionary, and lands on
// the student's dictionary right away (docs/project.md §4). The index route sends users with
// nothing to show to onboarding.
export const Route = createFileRoute('/_app/d/$dictionaryId')({
  component: Home,
});

function Home() {
  const { dictionaryId } = Route.useParams();
  const t = useTranslations('home');
  const locale = useLocale();
  const navigate = useNavigate();
  const desktop = useIsDesktop();
  const online = useOnline();
  const dictionaries = useDictionaries();
  const dictionary = dictionaries.find((d) => d.id === dictionaryId);
  const cards = useCards(dictionary);
  const pending = usePendingAdds(dictionaryId);
  const [switcher, setSwitcher] = useState(false);

  useEffect(() => {
    if (!dictionary) return;
    storage.setLastDictionary(dictionary.id);
    // Events inside a dictionary carry its language pair and whose it is
    setAnalyticsContext({
      dictionary_id: dictionary.id,
      target_lang: dictionary.targetLang,
      base_lang: dictionary.baseLang,
      role: dictionary.role,
    });
  }, [dictionary]);
  // Dictionary list loaded and this id is not ours (left, deleted account) → home
  useEffect(() => {
    if (dictionaries.length > 0 && !dictionary) void navigate({ to: '/', replace: true });
  }, [dictionaries, dictionary, navigate]);

  const wordCount = useMemo(() => cards?.length ?? dictionary?.wordCount ?? 0, [cards, dictionary]);
  const onAddManually = (headword: string) => void navigate({ to: '/d/$dictionaryId/add', params: { dictionaryId }, search: { headword } });

  if (!dictionary) return <div className="p-6 text-ink-secondary">…</div>;

  const title = dictionaryTitle(dictionary, locale);
  const sub = studentLabel(dictionary, t as never);
  const isEmpty = (cards?.length ?? 0) === 0 && pending.length === 0;

  const empty = (
    <div className="flex flex-grow flex-col items-center justify-center gap-3 px-10 pb-[120px] text-center desktop:pb-10" data-testid="empty-state">
      <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-surface text-accent">
        <Book size={32} />
      </span>
      <div className="mt-2 text-[22px] font-bold leading-7 tracking-[-0.3px]">{t('emptyTitle')}</div>
      <div className="max-w-[420px] text-[15px] leading-[21px] text-ink-secondary">
        {t(desktop ? 'emptyTextDesktop' : 'emptyText', { target: languageName(dictionary.targetLang, locale), base: languageName(dictionary.baseLang, locale) })}
      </div>
    </div>
  );

  if (desktop) {
    return (
      <div className="flex h-full">
        <nav aria-label={t('dictionaries')} className="flex w-[260px] flex-shrink-0 flex-col border-r border-[#D9D9DE] bg-surface-muted px-3 pb-3">
          <div className="flex h-[52px] items-center gap-2 px-2">
            <span className="h-3 w-3 rounded-full bg-accent" />
            <span className="text-[14px] font-bold tracking-[-0.2px]">Solungo</span>
          </div>
          <DictionaryLists dictionaries={dictionaries} currentId={dictionaryId} variant="sidebar" />
        </nav>
        <div className="flex min-w-0 flex-grow flex-col">
          <div className="flex h-[52px] flex-shrink-0 items-center gap-4 border-b border-border-soft px-5">
            <div className="flex min-w-[200px] flex-col">
              <span className="text-[15px] font-bold leading-[18px]">{title}</span>
              <span className="flex items-center gap-2 text-[12px] leading-[15px] text-ink-secondary">
                {sub ? `${sub} · ` : ''}
                {t('words', { count: wordCount })}
                <OfflineBadge />
              </span>
            </div>
            <div className="flex flex-grow justify-center">
              <DesktopOmnibox dictionary={dictionary} cards={cards ?? []} />
            </div>
            <div className="flex min-w-[200px] items-center justify-end gap-1">
              {dictionary.role === 'owner' && (
                <Link to="/d/$dictionaryId/settings" params={{ dictionaryId }} className="icon-btn text-ink-secondary" aria-label={t('dictionarySettings')}>
                  <Book size={20} />
                </Link>
              )}
              <Link to="/settings" className="icon-btn text-ink-secondary" aria-label={t('settings')} data-testid="settings-link">
                <Gear size={22} />
              </Link>
            </div>
          </div>
          <div className="flex flex-grow flex-col overflow-y-auto px-7 pb-10 pt-[22px]">
            <RetiredNotices dictionaryId={dictionaryId} />
            {isEmpty && cards ? empty : <WordList dictionaryId={dictionaryId} cards={cards ?? []} pending={pending} onAddManually={onAddManually} />}
          </div>
        </div>
        <Outlet />
      </div>
    );
  }

  return (
    <div className="relative flex min-h-full flex-col">
      <div className="pt-safe flex flex-col gap-0.5 px-4 pt-1">
        <div className="flex items-center justify-between gap-2">
          <button type="button" aria-label={t('switchDictionary')} data-testid="switcher" className="flex min-h-[44px] items-center gap-1.5 text-ink" onClick={() => setSwitcher(true)}>
            <span className="text-[34px] font-bold leading-10 tracking-[-0.4px]">{title}</span>
            <span className="mt-1 flex h-6 w-6 items-center justify-center rounded-full bg-control text-[#3A3A3C]">
              <ChevronDown />
            </span>
          </button>
          <div className="flex items-center gap-1">
            <Link to="/settings" className="icon-btn text-accent" aria-label={t('settings')} data-testid="settings-link">
              <Gear />
            </Link>
          </div>
        </div>
        <div className="flex items-center gap-2.5 text-[15px] leading-5 text-ink-secondary">
          <span>
            {sub ? `${sub} · ` : ''}
            {t('words', { count: wordCount })}
          </span>
          <OfflineBadge />
        </div>
      </div>
      <div className="flex flex-grow flex-col px-4 pb-[130px] pt-2">
        <RetiredNotices dictionaryId={dictionaryId} />
        {isEmpty && cards ? empty : <WordList dictionaryId={dictionaryId} cards={cards ?? []} pending={pending} onAddManually={onAddManually} />}
      </div>
      {/* Floating search pill under the thumb */}
      <div className="pointer-events-none fixed inset-x-0 bottom-0 z-20 flex h-[140px] items-end justify-center bg-gradient-to-b from-transparent via-canvas/90 to-canvas pb-[max(env(safe-area-inset-bottom),34px)]">
        <Link
          to="/d/$dictionaryId/search"
          params={{ dictionaryId }}
          aria-label={t('searchOrAddWord')}
          data-testid="search-pill"
          className="pointer-events-auto flex h-14 items-center gap-2.5 rounded-full bg-surface pl-5 pr-6 text-[17px] font-semibold text-accent shadow-float"
        >
          <Search />
          <span>{online ? t('searchOrAdd') : t('searchOrAddWord').split(' ')[0]}</span>
        </Link>
      </div>
      {switcher && (
        <Sheet onClose={() => setSwitcher(false)} label={t('dictionaries')}>
          <SheetBar onClose={() => setSwitcher(false)} title={t('dictionaries')} />
          <div className="overflow-y-auto pb-8">
            <DictionaryLists dictionaries={dictionaries} currentId={dictionaryId} variant="sheet" onPick={() => setSwitcher(false)} />
          </div>
        </Sheet>
      )}
      <Outlet />
    </div>
  );
}
